/**
 * Finding GROUPING — the difference between a report and a wall.
 *
 * A compute scan of a Windows image returns hundreds of findings that are the SAME
 * observation repeated per file. Measured on a real scan in ordb: 10,733 findings
 * collapse to 2,666 distinct titles, and the residue is almost entirely
 * "Suspicious API imports in <this dll>" once per DLL. The AD1 report shows 171
 * consecutive identical rows reading "YARA heuristic match: Ransomware_Extension_Change".
 *
 * A list like that cannot be read, and worse, its LENGTH is what the user perceives
 * as severity — 609 rows feels like 609 problems when it is really three.
 *
 * So findings are grouped by what was observed, not by where. Each group carries its
 * members, so nothing is hidden: expanding a group lists every file.
 *
 * ── The normalisation, and why it is deliberately narrow ─────────────────────────
 *
 * Only a trailing FILENAME is stripped, and only when it ends in a known binary/script
 * extension. Two title shapes carry one:
 *
 *   "Suspicious API imports in uireng.dll"            (binary_analysis, " in ")
 *   "Executable in suspicious location: $I39A86R.exe" (filesystem_anomaly, ": ")
 *
 * The colon form is shared with ClamAV, where the tail is a SIGNATURE and is the whole
 * point of the finding:
 *
 *   "Malware detected: Heuristics.Broken.Executable"
 *   "Potentially-unwanted application: PUA.Win.Exploit.CVE_2012_1461-1"
 *
 * The extension whitelist is what separates them: `.Executable` and `-1` are not file
 * extensions, so ClamAV signatures survive intact and keep their own group per signature
 * — which is correct, because two different signatures are two different findings.
 *
 * Everything else (YARA rule names, event-log titles, AI-written titles) already groups
 * on its exact title and is left untouched.
 */

import type { DetectionClass } from './scan-classification'

// Extensions that mark a trailing token as a FILE rather than a signature or rule name.
const FILE_EXT = /\.(exe|dll|mui|sys|ocx|scr|bat|cmd|ps1|psm1|vbs|js|jar|msi|msu|cab|com|cpl|drv|efi|node|so|bin|dat|db|tmp|log|conf|cfg|ini|xml|json|txt)$/i

// A trailing token that is an OPAQUE PER-FILE IDENTIFIER: a hex digest or a GUID. These
// appear as "Executable in hidden directory: 30671b8845cc55922a47b10af31b6a4905ef0e" —
// 4,373 of them in one real scan, every one a distinct group under a filename-only rule.
// They are never signatures: a ClamAV signature (Heuristics.Broken.Executable) has dots
// and mixed case, a YARA rule (Ransomware_Extension_Change) has underscores and words.
const OPAQUE_ID = /^(?:[0-9a-f]{12,}|\{?[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\}?)$/i

// "<observation> in <tail>" or "<observation>: <tail>" — nothing else.
const TRAILING_FILE = /^(.*?)(?:\s+in\s+|:\s*)([^\s:/\\]+)$/i

export interface FindingGroup {
    key: string
    /** The observation with the per-file tail removed, e.g. "Suspicious API imports". */
    label: string
    engine: string
    detectionClass: DetectionClass
    /** Highest confidence seen across members (high > medium > low). */
    confidence: string | null
    /** Highest severity seen across members — secondary, never the headline. */
    severity: string | null
    /** MITRE technique, when every member agrees on one. */
    mitre: string | null
    mitreName: string | null
    /** Original findings, each keeping its index so expand state stays stable. */
    members: Array<{ f: any; i: number }>
    /** true when the group is >1 member, i.e. the label was actually collapsed. */
    collapsed: boolean
}

/**
 * Strip a trailing filename from a finding title. Returns the title unchanged when the
 * tail is not a filename (a ClamAV signature, a YARA rule, free text).
 */
export function normaliseTitle(title: string): string {
    const raw = String(title || '').trim()
    if (!raw) return ''
    const m = raw.match(TRAILING_FILE)
    if (!m) return raw
    const [, head, tail] = m
    if (!FILE_EXT.test(tail) && !OPAQUE_ID.test(tail)) return raw
    if (!head || head.trim().length < 3) return raw
    return head.trim()
}

const CONFIDENCE_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 }
const SEVERITY_RANK: Record<string, number> = { critical: 5, high: 4, medium: 3, low: 2, info: 1 }

function best(values: Array<string | null | undefined>, rank: Record<string, number>): string | null {
    let winner: string | null = null
    let score = 0
    for (const v of values) {
        const k = String(v || '').toLowerCase()
        const s = rank[k] || 0
        if (s > score) { score = s; winner = k }
    }
    return winner
}

/**
 * Group findings by (engine, detection class, normalised title).
 *
 * `classOf` is injected so the caller's detection_class rules stay in one place.
 * Order is preserved: groups appear in the order their first member appeared, so a
 * caller that pre-sorted by confidence keeps that sort.
 */
export function groupFindings(
    indexed: Array<{ f: any; i: number }>,
    classOf: (f: any) => DetectionClass,
): FindingGroup[] {
    const byKey = new Map<string, FindingGroup>()

    for (const item of indexed) {
        const { f } = item
        const engine = String(f?.engine || 'unknown')
        const detectionClass = classOf(f)
        const label = normaliseTitle(f?.title) || String(f?.title || 'Untitled finding')
        const key = `${engine}|${detectionClass}|${label}`

        let g = byKey.get(key)
        if (!g) {
            g = {
                key, label, engine, detectionClass,
                confidence: null, severity: null, mitre: null, mitreName: null,
                members: [], collapsed: false,
            }
            byKey.set(key, g)
        }
        g.members.push(item)
    }

    const out: FindingGroup[] = []
    for (const g of byKey.values()) {
        g.confidence = best(g.members.map((m) => m.f?.confidence), CONFIDENCE_RANK)
        g.severity = best(g.members.map((m) => m.f?.severity), SEVERITY_RANK)
        // Only claim a technique when the whole group agrees — a mixed group has none.
        const techniques = new Set(g.members.map((m) => m.f?.details?.mitre_attack).filter(Boolean))
        if (techniques.size === 1) {
            g.mitre = String(Array.from(techniques)[0])
            g.mitreName = g.members.find((m) => m.f?.details?.mitre_attack)?.f?.details?.mitre_technique || null
        }
        g.collapsed = g.members.length > 1
        out.push(g)
    }
    return out
}

/**
 * The headline clusters for the Overview — "what was flagged", largest first.
 * Confirmed detections always outrank indicators regardless of size: one confirmed
 * detection matters more than four hundred heuristic flags.
 */
export function topClusters(groups: FindingGroup[], limit = 4): FindingGroup[] {
    const weight = (g: FindingGroup) =>
        g.detectionClass === 'confirmed' ? 2 : g.detectionClass === 'indicator' ? 1 : 0
    return [...groups]
        .sort((a, b) => (weight(b) - weight(a)) || (b.members.length - a.members.length))
        .slice(0, limit)
}
